import { CommonModule } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  computed,
  ElementRef,
  inject,
  input,
  model,
  output,
  signal,
  DOCUMENT
} from '@angular/core';
import { isDefinedNotEmpty } from '@softline/core';
import { IconComponent } from '../../components/icon/icon.component';
import { toObservable } from '@angular/core/rxjs-interop';
import { VirtualKeyboardInputType } from '../directives/virtual-keyboard.directive';

@Component({
  selector: 'soft-virtual-keyboard',
  imports: [CommonModule, IconComponent],
  templateUrl: './virtual-keyboard.component.html',
  styleUrl: './virtual-keyboard.component.css',
  changeDetection: ChangeDetectionStrategy.OnPush,
  host: {
    class: 'soft-virtual-keyboard',
  },
})
export class VirtualKeyboardComponent {
  private readonly elementRef = inject(ElementRef);
  private readonly document = inject(DOCUMENT);
  readonly value = model<string | null>();

  readonly cursorIndex = model<number | undefined>();

  readonly inputType = model<VirtualKeyboardInputType>('full');

  readonly value$ = toObservable(this.value);

  readonly isNumericMode = signal(false);
  readonly isShiftActive = signal(false);
  readonly isCapsLockActive = signal(false);
  readonly isSymbolMode = signal(false);

  readonly onEnter = output<boolean>();

  protected programmaticCursorIndex: number | undefined = undefined;

  readonly columns = computed(() => {
    if (this.isNumericMode()) {
      return 10;
    }
    if (this.isSymbolMode()) {
      return 10;
    }
    return 11;
  });

  readonly displayedRows = computed<string[][]>(() => {
    const rows = this.isNumericMode() ? this.numericRows : this.alphabetRows;
    if (this.isShiftActive() || this.isCapsLockActive()) {
      return rows.map((row) => row.map((key) => key.toUpperCase()));
    }
    if (this.isSymbolMode()) {
      return this.symbolRows;
    }
    return rows;
  });

  readonly alphabetRows = [
    ['q', 'w', 'e', 'r', 't', 'z', 'u', 'i', 'o', 'p', 'ü'],
    ['a', 's', 'd', 'f', 'g', 'h', 'j', 'k', 'l', 'ö', 'ä'],
    ['y', 'x', 'c', 'v', 'b', 'n', 'm'],
  ];

  readonly numericRows = [
    ['1', '2', '3', '4', '5', '6', '7', '8', '9', '0'],
    ['-', '/', ':', ';', '(', ')', '€', '&', '@', '"'],
    ['.', ',', '?', '!', "'"],
  ];

  readonly symbolRows = [
    ['[', ']', '{', '}', '#', '%', '^', '*', '+', '='],
    ['_', '\\', '|', ' ~', '<', '>', '$', '£', '¥', '•'],
    ['.', ',', '?', '!', "'"],
  ];


  readonly displayedNumpadRowsCols = computed<{rows: string[][]}>(() => {
    const numpad = this.numpad;
    return numpad;
  });

  readonly numpad = {
    rows: [
      ['7', '8', '9'],
      ['4', '5', '6'],
      ['1', '2', '3'],
      ['0', '00', ','],
    ]
  };

  readonly knownKeys = [
    ...this.alphabetRows.flat(),
    ...this.numericRows.flat(),
    ...this.symbolRows.flat(),
    ...this.numpad.rows.flat(),
  ];

  isCommaDisabled(key: string): boolean {
    return this.inputType() === 'number' && key === ','
  }

  keyPress(key: string, cursorIndex?: number | undefined, fireEvent = true): void {
    if (fireEvent) {
      this.document.dispatchEvent(
        new KeyboardEvent('keydown', {
          key,
        })
      );
    }

    // use the internal cursor-index when not provided
    // usually the cursor index is only provided when using an external keyboard
    if (cursorIndex === undefined) {
      cursorIndex = this.cursorIndex();
    } else {
      this.cursorIndex.set(cursorIndex);
    }

    if (key === 'Backspace') {
      if (isDefinedNotEmpty(this.document.getSelection()?.toString())) {
        this.value.set('');
        this.document.getSelection()?.empty();
        this.cursorIndex.set(0);
      } else {
        if (cursorIndex !== undefined) {
          const value = this.value();
          const start = value?.slice(0, cursorIndex);
          const end = value?.slice(cursorIndex);
          this.value.update(() => `${start?.slice(0, -1)}${end}`);
        } else {
          this.value.update((val) => (val ? val.slice(0, -1) : null));
        }

        this.cursorIndex.update((i) => Math.max((i ?? 0) - 1, 0));
      }
    } else if (key === 'Space' || key === ' ') {
      if (isDefinedNotEmpty(this.document.getSelection()?.toString())) {
        this.value.update(() => ' ');
        this.document.getSelection()?.empty();
        this.cursorIndex.set(1);
      } else {
        const value = this.value();

        if (cursorIndex !== undefined && cursorIndex < (value?.length ?? 0)) {
          const start = value?.slice(0, cursorIndex);
          const end = value?.slice(cursorIndex);
          this.value.update(() => `${start} ${end}`);
        } else {
          this.value.update((val) => `${val ?? ''} `);
        }

        this.cursorIndex.update((i) => (i ?? 0) + 1);
      }
    } else if (key === 'Enter') {
      this.value.update((val) => `${val ?? ''}`);
      if (fireEvent) {
        this.onEnter.emit(fireEvent);
        this.document.dispatchEvent(
          new SubmitEvent('softKeyboardSubmit', {
            submitter: this.elementRef.nativeElement,
          })
        );
      }
    } else if (this.knownKeys.includes(key.toLowerCase())) {
      // when there is a selection, we replace the value instead of appending it (overwrite)
      if (isDefinedNotEmpty(this.document.getSelection()?.toString())) {
        this.value.set(key);
        this.document.getSelection()?.empty();
        this.cursorIndex.set(1);
      } else {
        const value = this.value();

        if (cursorIndex !== undefined && cursorIndex < (value?.length ?? 1) - 1) {
          const start = value?.slice(0, cursorIndex);
          const end = value?.slice(cursorIndex);
          this.value.update(() => `${start}${key}${end}`);
        } else {
          this.value.update((val) => `${val ?? ''}${key}`);
        }

        this.cursorIndex.update((i) => (i ?? 0) + key.length);
      }
    }

    if (this.isShiftActive) {
      this.isShiftActive.set(false);
    }
  }

  toggleShift(): void {
    this.isShiftActive.set(!this.isShiftActive());
  }

  toggleCapsLock(): void {
    this.isCapsLockActive.set(!this.isCapsLockActive());
  }

  toggleNumericMode(): void {
    this.isNumericMode.set(!this.isNumericMode());

    if (this.isSymbolMode()) {
      this.isSymbolMode.set(false);
    }

    this.resetShiftAndCapsLock();
  }

  toggleSymbolMode(): void {
    this.isSymbolMode.set(!this.isSymbolMode());
    this.resetShiftAndCapsLock();
  }

  resetShiftAndCapsLock(): void {
    this.isShiftActive.set(false);
    this.isCapsLockActive.set(false);
  }
}
